import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollText, ShieldAlert, CheckCircle2 } from "lucide-react";
import { formatError as fe } from "@/lib/api";

/**
 * Rules acceptance modal.
 * Props:
 *  - open: boolean
 *  - onOpenChange: fn(bool)
 *  - auctionId: string
 *  - facilityName: string (optional)
 *  - onAccepted: callback fn() after acceptance persisted server-side
 */
export default function RulesModal({ open, onOpenChange, auctionId, onAccepted }) {
  const [rules, setRules] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !auctionId) return;
    setChecked(false);
    api.get(`/auctions/${auctionId}/rules`)
      .then((r) => setRules(r.data))
      .catch(() => toast.error("Could not load rules"));
  }, [open, auctionId]);

  const accept = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await api.post(`/auctions/${auctionId}/rules/accept`, {});
      toast.success("Rules accepted — you can now bid");
      onOpenChange(false);
      onAccepted && onAccepted();
    } catch (e) {
      toast.error(fe(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="rules-modal">
        <DialogHeader className="p-6 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
              <ScrollText className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">Before You Bid</div>
              <DialogTitle className="font-display text-2xl uppercase tracking-tight">Auction Rules & Terms</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-zinc-400 pt-2">
            Read all rules carefully. Placing a bid is a legally binding contract to purchase.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          {!rules ? (
            <div className="text-zinc-500 text-sm">Loading rules…</div>
          ) : (
            <>
              <div className="bg-red-500/5 border border-red-500/40 p-4 flex gap-3">
                <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-red-500 font-display font-bold text-sm uppercase tracking-wider">Non-Payment = Account Suspension</div>
                  <div className="text-xs text-zinc-400 mt-1">All bids are binding. If you win, you must pay within 24 hours or your account will be suspended and you may face legal action.</div>
                </div>
              </div>

              {rules.custom_rules && rules.custom_rules.trim() && (
                <div className="bg-orange-500/5 border border-orange-500/40 p-4">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">
                    Facility-Specific Rules {rules.facility_name ? `— ${rules.facility_name}` : ""}
                  </div>
                  <div className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed">
                    {rules.custom_rules}
                  </div>
                </div>
              )}

              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 pt-2">Platform Rules</div>
              <ol className="space-y-3">
                {rules.platform_rules.map((r, i) => (
                  <li key={r.id} className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <div className="flex items-start gap-3">
                      <div className="font-display text-xl font-black text-orange-500 tabular-nums leading-none shrink-0 min-w-[28px]">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="font-display font-bold uppercase text-sm tracking-tight text-white">{r.title}</div>
                        <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{r.text}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>

        <div className="p-6 pt-4 border-t border-zinc-800 bg-black/50">
          <label className="flex items-start gap-3 cursor-pointer select-none mb-4">
            <Checkbox
              data-testid="rules-agree-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(!!v)}
              className="rounded-none border-zinc-700 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-500 mt-0.5"
            />
            <span className="text-sm text-zinc-300 leading-relaxed">
              I have read and agree to all {rules?.platform_rules?.length || 11} platform rules
              {rules?.custom_rules?.trim() ? ", plus the facility-specific rules above" : ""},
              and I understand that placing a bid creates a legally binding purchase contract.
            </span>
          </label>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="rules-cancel"
              className="rounded-none bg-transparent border-zinc-700 hover:bg-zinc-900 text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
            <Button
              onClick={accept}
              disabled={!checked || submitting}
              data-testid="rules-accept"
              className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-11 px-6 text-xs uppercase tracking-widest font-semibold disabled:opacity-40"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {submitting ? "Confirming…" : "I Agree — Enable Bidding"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
